import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Modal, Space, Spin, message } from "antd";
import dayjs from "dayjs";
import { serverFetch } from "@core/server";
import {
  addSprZalGrafUsl,
  clearSprZalGrafDay,
  createSprZalGrafSession,
  fetchSprZalGraf,
  fetchSprZalGrafUsl
} from "./spr-zal-api";
import { readGrafDayClipboard, writeGrafDayClipboard } from "./spr-zal-graf-clipboard";
import SprZalGrafFactModal from "./spr-zal-graf-fact-modal";
import "./spr-zal-graf.css";

const { RangePicker } = DatePicker;

/** dayjs().day(): 0 = вс … 6 = сб */
const WEEKDAYS = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];

const defaultPeriod = () => [dayjs().startOf("month"), dayjs().endOf("month")];

const eachDay = (beg, end) => {
  const days = [];
  let d = beg.startOf("day");
  const last = end.startOf("day");
  while (d.isBefore(last) || d.isSame(last, "day")) {
    days.push(d);
    d = d.add(1, "day");
  }
  return days;
};

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatHm = (value) => {
  const d = dayjs(value);
  return d.isValid() ? d.format("HH:mm") : "09:00";
};

const parseHmOnDate = (hm, day) => {
  const m = String(hm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return day.hour(9).minute(0).second(0).millisecond(0);
  return day.hour(Number(m[1])).minute(Number(m[2])).second(0).millisecond(0);
};

const timeToLocalIso = (date, time) => {
  if (!date?.isValid?.() || !time?.isValid?.()) return null;
  return date
    .hour(time.hour())
    .minute(time.minute())
    .second(0)
    .millisecond(0)
    .format("YYYY-MM-DDTHH:mm:ss");
};

/** График работы группы кабинетов — как Planner2 в Delphi: кабинеты слева, даты сверху. */
const SprZalGrafGroup = ({ zal, open }) => {
  const [period, setPeriod] = useState(defaultPeriod);
  const [children, setChildren] = useState([]);
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [factOpen, setFactOpen] = useState(false);

  const load = useCallback(() => {
    if (!zal?.idZal || !period?.[0] || !period?.[1]) return;
    setLoading(true);
    setError("");
    const beg = period[0].format("YYYY-MM-DD");
    const end = period[1].format("YYYY-MM-DD");
    serverFetch(`sprs/zal/${zal.idZal}/graf-group`, { queryParams: { beg, end } })
      .then((data) => {
        setChildren(Array.isArray(data?.children) ? data.children : []);
        setCells(Array.isArray(data?.cells) ? data.cells : []);
      })
      .catch((e) => setError(e.message || "Не удалось загрузить график группы"))
      .finally(() => setLoading(false));
  }, [zal?.idZal, period]);

  useEffect(() => {
    if (!open) return;
    setPeriod(defaultPeriod());
    setSelected(null);
    setFactOpen(false);
  }, [open, zal?.idZal]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const cellMap = useMemo(() => {
    const map = new Map();
    cells.forEach((c) => {
      const idZal = c.idZal ?? c.IdZal;
      const fdate = dayjs(c.fdate ?? c.Fdate).format("YYYY-MM-DD");
      map.set(`${idZal}|${fdate}`, c);
    });
    return map;
  }, [cells]);

  const days = useMemo(() => {
    if (!period?.[0] || !period?.[1]) return [];
    return eachDay(period[0], period[1]);
  }, [period]);

  const rows = useMemo(
    () =>
      children.map((ch) => ({
        idZal: ch.idZal ?? ch.IdZal,
        name: ch.name ?? ch.Name ?? ""
      })),
    [children]
  );

  const selectedCell = useMemo(() => {
    if (!selected) return null;
    return cellMap.get(`${selected.idZal}|${selected.dateKey}`) || null;
  }, [selected, cellMap]);

  const selectedZalName = useMemo(() => {
    if (!selected) return "";
    return rows.find((r) => r.idZal === selected.idZal)?.name || "";
  }, [selected, rows]);

  const hasSelection = Boolean(selected);
  const hasFilledSelection = Boolean(selected && selectedCell);

  const onClear = useCallback(() => {
    if (!selected) return;
    Modal.confirm({
      title: "Очистить график на день?",
      content: `${selectedZalName || "Кабинет"}, ${dayjs(selected.dateKey).format("DD.MM.YYYY")}`,
      okText: "Очистить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: () =>
        clearSprZalGrafDay(selected.idZal, selected.dateKey)
          .then(() => {
            message.success("День очищен");
            setSelected(null);
            load();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось очистить");
            return Promise.reject(e);
          })
    });
  }, [selected, selectedZalName, load]);

  const onCopy = useCallback(async () => {
    if (!selected || !selectedCell) return;
    setBusy(true);
    try {
      const sessions = await fetchSprZalGraf(selected.idZal, selected.dateKey, selected.dateKey);
      const list = Array.isArray(sessions) ? sessions : [];
      if (!list.length) {
        message.warning("На выбранный день нет сеансов");
        return;
      }
      const payloadSessions = [];
      for (const row of list) {
        const idStr = toNumber(pick(row, "idStr", "IdStr"));
        const uslRows = idStr != null ? await fetchSprZalGrafUsl(selected.idZal, idStr) : [];
        payloadSessions.push({
          cntMax: toNumber(pick(row, "cntMax", "CntMax")) ?? 1,
          idSotr: toNumber(pick(row, "idSotr", "IdSotr")) || 0,
          idSotrRun: toNumber(pick(row, "idSotrRun", "IdSotrRun")) || 0,
          timeBeg: formatHm(pick(row, "timeBeg", "TimeBeg")),
          timeEnd: formatHm(pick(row, "timeEnd", "TimeEnd")),
          usl: (Array.isArray(uslRows) ? uslRows : [])
            .map((u) => toNumber(pick(u, "idUsl", "IdUsl")))
            .filter((id) => id > 0)
        });
      }
      await writeGrafDayClipboard({ sessions: payloadSessions });
      message.success(`Скопировано сеансов: ${payloadSessions.length}`);
    } catch (e) {
      message.error(e.message || "Не удалось скопировать");
    } finally {
      setBusy(false);
    }
  }, [selected, selectedCell]);

  const onPaste = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const data = await readGrafDayClipboard();
      const existing = await fetchSprZalGraf(selected.idZal, selected.dateKey, selected.dateKey);
      if (Array.isArray(existing) && existing.length) {
        message.warning("На этот день уже есть график — вставка невозможна");
        return;
      }
      const day = dayjs(selected.dateKey);
      let created = 0;
      for (const session of data.sessions || []) {
        const timeBeg = parseHmOnDate(session.timeBeg, day);
        const timeEnd = parseHmOnDate(session.timeEnd, day);
        const row = await createSprZalGrafSession(selected.idZal, {
          fdate: selected.dateKey,
          timeBeg: timeToLocalIso(day, timeBeg),
          timeEnd: timeToLocalIso(day, timeEnd),
          cntMax: session.cntMax,
          idSotr: session.idSotr,
          idSotrRun: session.idSotrRun
        });
        const newId = toNumber(pick(row, "idStr", "IdStr"));
        if (newId != null) {
          for (const idUsl of session.usl || []) {
            try {
              await addSprZalGrafUsl(selected.idZal, newId, idUsl);
            } catch {
              /* ignore duplicates */
            }
          }
          created += 1;
        }
      }
      message.success(created ? `Вставлено сеансов: ${created}` : "Нечего вставлять");
      load();
    } catch (e) {
      message.error(e.message || "Не удалось вставить");
    } finally {
      setBusy(false);
    }
  }, [selected, load]);

  const onFact = useCallback(() => {
    if (!selected) return;
    setFactOpen(true);
  }, [selected]);

  return (
    <div className="spr-zal-graf">
      <div className="spr-zal-graf__period">
        <span>Период для планирования с</span>
        <RangePicker
          value={period}
          onChange={(v) => setPeriod(v || defaultPeriod())}
          format="DD.MM.YYYY"
          allowClear={false}
        />
        <Space size={4} style={{ flex: "none" }}>
          <Button danger disabled={!hasFilledSelection || busy} onClick={onClear}>
            Очистить
          </Button>
          <Button disabled={!hasFilledSelection || busy} loading={busy} onClick={onCopy}>
            Копировать
          </Button>
          <Button disabled={!hasSelection || busy} loading={busy} onClick={onPaste}>
            Вставить
          </Button>
          <Button disabled={!hasSelection || busy} onClick={onFact}>
            Факт
          </Button>
        </Space>
      </div>
      {error ? <Alert type="error" showIcon message={error} className="spr-zal-graf__error" /> : null}
      <div className="spr-zal-graf__body">
        <Spin spinning={loading}>
          {!rows.length && !loading ? (
            <div className="spr-zal-graf-group__empty">Нет дочерних кабинетов</div>
          ) : (
            <div className="spr-zal-graf-group">
              <table className="spr-zal-graf-group__table">
                <thead>
                  <tr>
                    <th className="spr-zal-graf-group__corner" />
                    {days.map((d) => {
                      const weekend = d.day() === 0 || d.day() === 6;
                      return (
                        <th
                          key={d.format("YYYY-MM-DD")}
                          className={`spr-zal-graf-group__day${weekend ? " spr-zal-graf-group__day--weekend" : ""}`}
                        >
                          <div className="spr-zal-graf-group__day-label">
                            <span className="spr-zal-graf-group__day-num">{d.format("D")}</span>
                            <span className="spr-zal-graf-group__day-month">{d.format("MM")}</span>
                            <span className="spr-zal-graf-group__day-weekday">{WEEKDAYS[d.day()]}</span>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.idZal}>
                      <th className="spr-zal-graf-group__zal" title={row.name}>
                        {row.name}
                      </th>
                      {days.map((d) => {
                        const dateKey = d.format("YYYY-MM-DD");
                        const weekend = d.day() === 0 || d.day() === 6;
                        const cell = cellMap.get(`${row.idZal}|${dateKey}`);
                        const cnt = cell ? (cell.cntMax ?? cell.CntMax ?? "") : "";
                        const cntPlan = cell ? Number(cell.cntPlan ?? cell.CntPlan ?? 0) : 0;
                        const planTotal = cell ? Number(cell.planTotal ?? cell.PlanTotal ?? 0) : 0;
                        const planDone = cell ? Number(cell.planDone ?? cell.PlanDone ?? 0) : 0;
                        let factTone = "none";
                        if (planTotal > 0) {
                          if (planDone <= 0) factTone = "none";
                          else if (planDone < planTotal) factTone = "partial";
                          else factTone = "all";
                        }
                        const isSelected =
                          selected?.idZal === row.idZal && selected?.dateKey === dateKey;
                        const classNames = [
                          "spr-zal-graf-group__cell",
                          weekend ? "spr-zal-graf-group__cell--weekend" : "",
                          isSelected ? "spr-zal-graf-group__cell--selected" : "",
                          cell ? "spr-zal-graf-group__cell--filled" : ""
                        ]
                          .filter(Boolean)
                          .join(" ");
                        return (
                          <td
                            key={dateKey}
                            className={classNames}
                            onClick={() => setSelected({ idZal: row.idZal, dateKey })}
                          >
                            {cnt !== "" ? (
                              <span className="spr-zal-graf-group__cell-main">{cnt}</span>
                            ) : null}
                            {cntPlan > 0 ? (
                              <span
                                className={`spr-zal-graf-group__badge spr-zal-graf-group__badge--${factTone}`}
                                title={`Записано: ${cntPlan}`}
                              >
                                {cntPlan}
                              </span>
                            ) : null}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Spin>
      </div>

      <SprZalGrafFactModal
        open={factOpen}
        idZal={selected?.idZal}
        fdate={selected?.dateKey}
        zalName={selectedZalName}
        onClose={() => setFactOpen(false)}
        onChanged={load}
      />
    </div>
  );
};

export default SprZalGrafGroup;
